import { useEffect, useRef, useState } from 'react';
import { Komunikat, Przycisk, PoleTekstowe } from './ui';

/**
 * Skan naklejki QR.
 *
 * Bez biblioteki: serwisanci pilotażu mają Androidy, a Chrome ma wbudowany
 * `BarcodeDetector` (CLAUDE.md §9). Dodawanie 300 kB dekodera do aplikacji, która
 * ma działać offline, byłoby kosztem bez powodu.
 *
 * Gdzie API nie ma (przeglądarka biura, Safari), zostaje przepisanie kodu z naklejki —
 * i to jest jedyny tryb, który musi działać zawsze.
 */

interface WykrytyKod {
  rawValue: string;
}

interface DetektorKodow {
  detect: (zrodlo: CanvasImageSource) => Promise<WykrytyKod[]>;
}

type KonstruktorDetektora = new (opcje: { formats: string[] }) => DetektorKodow;

function detektor(): KonstruktorDetektora | null {
  const globalne = window as unknown as { BarcodeDetector?: KonstruktorDetektora };
  return globalne.BarcodeDetector ?? null;
}

export function SkanQr({
  onToken,
  onZamknij,
}: {
  onToken: (token: string) => void;
  onZamknij: () => void;
}) {
  const wideo = useRef<HTMLVideoElement>(null);
  const [blad, setBlad] = useState<string | null>(null);
  const [reczny, setReczny] = useState('');
  const [kamera, setKamera] = useState(() => detektor() !== null);

  useEffect(() => {
    if (!kamera) return;

    const Detektor = detektor();
    if (!Detektor) return;

    let strumien: MediaStream | null = null;
    let zegar: number | undefined;
    let zatrzymane = false;

    const start = async (): Promise<void> => {
      try {
        strumien = await navigator.mediaDevices.getUserMedia({
          // Tylna kamera — serwisant celuje w maszynę, nie w siebie.
          video: { facingMode: { ideal: 'environment' } },
        });
      } catch {
        setKamera(false);
        setBlad('Brak dostępu do aparatu. Przepisz kod z naklejki.');
        return;
      }

      if (zatrzymane || !wideo.current) return;
      wideo.current.srcObject = strumien;
      await wideo.current.play().catch(() => undefined);

      const czytnik = new Detektor({ formats: ['qr_code'] });
      zegar = window.setInterval(() => {
        void (async () => {
          if (!wideo.current || wideo.current.readyState < 2) return;
          try {
            const kody = await czytnik.detect(wideo.current);
            const pierwszy = kody[0]?.rawValue;
            if (pierwszy) {
              window.clearInterval(zegar);
              onToken(pierwszy);
            }
          } catch {
            // Pojedyncza nieudana klatka nie jest błędem — kolejna może się udać.
          }
        })();
      }, 250);
    };

    void start();

    return () => {
      zatrzymane = true;
      window.clearInterval(zegar);
      strumien?.getTracks().forEach((sciezka) => sciezka.stop());
    };
  }, [kamera, onToken]);

  return (
    <div className="border border-n-100 bg-surface">
      <header className="flex min-h-44 items-center gap-13 border-b border-n-100 px-18 py-10">
        <h2 className="t-mono-micro mr-auto text-n-500">Skan naklejki</h2>
        <Przycisk type="button" onClick={onZamknij}>
          Zamknij
        </Przycisk>
      </header>

      <div className="p-18">
        {kamera && (
          <video
            ref={wideo}
            muted
            playsInline
            className="mb-13 block w-full max-w-[420px] border border-n-100 bg-n-900"
          />
        )}

        {blad && (
          <div className="mb-13">
            <Komunikat tresc={blad} rodzaj="info" />
          </div>
        )}

        <form
          onSubmit={(zdarzenie) => {
            zdarzenie.preventDefault();
            const token = reczny.trim();
            if (token) onToken(token);
          }}
          className="flex flex-wrap items-end gap-13"
        >
          <div className="min-w-[220px] flex-1">
            <PoleTekstowe
              etykieta="Kod z naklejki"
              value={reczny}
              onChange={(zdarzenie) => setReczny(zdarzenie.target.value)}
              autoCapitalize="characters"
              autoComplete="off"
              placeholder="np. K7F3M9QX2P"
              className="t-mono-data w-full border border-n-300 bg-surface px-13 text-n-900 uppercase"
            />
          </div>
          <Przycisk type="submit" disabled={!reczny.trim()}>
            Otwórz kartę
          </Przycisk>
        </form>
      </div>
    </div>
  );
}
